export const MAX_LIVE_LOG_STYLE_RUNS = 256;
export const MAX_LIVE_LOG_DISPLAY_BYTES = 4096;

export type LiveLogForeground = "black" | "red" | "green" | "yellow" | "blue" | "magenta" | "cyan" | "white";

export interface LiveLogTextRun {
  text: string;
  foreground?: LiveLogForeground;
  bold?: boolean;
}

export interface LiveLogANSIResult {
  sanitizedANSI: string;
  normalizedPlain: string;
  runs: LiveLogTextRun[];
  runsCapped: boolean;
}

interface Style {
  foreground?: LiveLogForeground;
  bold: boolean;
}

const COLORS: LiveLogForeground[] = ["black", "red", "green", "yellow", "blue", "magenta", "cyan", "white"];
const ESC = "\u001b";

function validSGR(raw: string): string[] | undefined {
  const params = raw === "" ? ["0"] : raw.split(";");
  if (params.some((p) => !/^(?:0|1|22|3[0-7]|39)$/.test(p))) return undefined;
  return params;
}

function sameStyle(a: LiveLogTextRun, style: Style): boolean {
  return a.foreground === style.foreground && Boolean(a.bold) === style.bold;
}

function applySGR(style: Style, params: string[]): void {
  for (const param of params) {
    const code = Number(param);
    if (code === 0) {
      style.foreground = undefined;
      style.bold = false;
    } else if (code === 1) style.bold = true;
    else if (code === 22) style.bold = false;
    else if (code >= 30 && code <= 37) style.foreground = COLORS[code - 30];
    else if (code === 39) style.foreground = undefined;
  }
}

function controlStringEnd(input: string, from: number): number {
  for (let i = from; i < input.length; i++) {
    const code = input.charCodeAt(i);
    if (code === 0x07 || code === 0x9c) return i + 1;
    if (code === 0x1b && input.charCodeAt(i + 1) === 0x5c) return i + 2;
  }
  return input.length;
}

function csiEnd(input: string, from: number): { end: number; params?: string[]; canonical?: string } {
  const start = from;
  for (let i = from; i < input.length; i++) {
    const code = input.charCodeAt(i);
    if (code >= 0x40 && code <= 0x7e) {
      if (input[i] !== "m") return { end: i + 1 };
      const raw = input.slice(start, i);
      const params = validSGR(raw);
      return params ? { end: i + 1, params, canonical: raw || "0" } : { end: i + 1 };
    }
    if (code < 0x20 || code > 0x3f) return { end: i + 1 };
  }
  return { end: input.length };
}

/**
 * 서버와 같은 닫힌 터미널 파서입니다. HTML은 만들지 않으며 OSC/DCS/unknown
 * payload를 버리고 foreground 8색과 bold만 구조화된 text run으로 남깁니다.
 */
export function parseLiveLogAnsi(input: string, maxRuns = MAX_LIVE_LOG_STYLE_RUNS): LiveLogANSIResult {
  const sanitized: string[] = [];
  const plain: string[] = [];
  const runs: LiveLogTextRun[] = [];
  const style: Style = { bold: false };
  let styledRuns = 0;
  let runsCapped = false;

  const write = (text: string) => {
    if (!text) return;
    sanitized.push(text);
    plain.push(text);
    const effective: Style = runsCapped ? { bold: false } : style;
    const last = runs.at(-1);
    const wouldStyle = Boolean(effective.bold || effective.foreground);
    if (wouldStyle && (!last || !sameStyle(last, effective)) && styledRuns >= maxRuns) {
      runsCapped = true;
      const unstyled = runs.at(-1);
      if (unstyled && !unstyled.bold && !unstyled.foreground) unstyled.text += text;
      else runs.push({ text });
      return;
    }
    if (last && sameStyle(last, effective)) last.text += text;
    else {
      const run: LiveLogTextRun = { text };
      if (effective.foreground) run.foreground = effective.foreground;
      if (effective.bold) run.bold = true;
      runs.push(run);
      if (wouldStyle) styledRuns++;
    }
  };

  for (let i = 0; i < input.length;) {
    const code = input.charCodeAt(i);
    if (code === 0x09) {
      write("\t");
      i++;
      continue;
    }
    if (code === 0x1b) {
      if (i + 1 >= input.length) break;
      const next = input.charCodeAt(i + 1);
      if (next === 0x5b) {
        const parsed = csiEnd(input, i + 2);
        i = parsed.end;
        if (parsed.params && parsed.canonical) {
          sanitized.push(`${ESC}[${parsed.canonical}m`);
          applySGR(style, parsed.params);
        }
        continue;
      }
      if (next === 0x5d || next === 0x50 || next === 0x58 || next === 0x5e || next === 0x5f) {
        i = controlStringEnd(input, i + 2);
        continue;
      }
      i++;
      while (i < input.length && input.charCodeAt(i) >= 0x20 && input.charCodeAt(i) <= 0x2f) i++;
      if (i < input.length && input.charCodeAt(i) >= 0x30 && input.charCodeAt(i) <= 0x7e) i++;
      continue;
    }
    if (code === 0x9b) {
      const parsed = csiEnd(input, i + 1);
      i = parsed.end;
      if (parsed.params && parsed.canonical) {
        sanitized.push(`${ESC}[${parsed.canonical}m`);
        applySGR(style, parsed.params);
      }
      continue;
    }
    if (code === 0x90 || code === 0x98 || code === 0x9d || code === 0x9e || code === 0x9f) {
      i = controlStringEnd(input, i + 1);
      continue;
    }
    if (code < 0x20 || code === 0x7f || (code >= 0x80 && code <= 0x9f)) {
      i++;
      continue;
    }
    let end = i + 1;
    while (end < input.length) {
      const next = input.charCodeAt(end);
      if (next < 0x20 || next === 0x7f || (next >= 0x80 && next <= 0x9f)) break;
      end++;
    }
    write(input.slice(i, end));
    i = end;
  }

  if (style.bold || style.foreground) sanitized.push(`${ESC}[0m`);
  return { sanitizedANSI: sanitized.join(""), normalizedPlain: plain.join(""), runs, runsCapped };
}

function utf8SizeAt(text: string, index: number): { size: number; width: number } {
  const cp = text.codePointAt(index) ?? 0;
  return { size: cp <= 0x7f ? 1 : cp <= 0x7ff ? 2 : cp <= 0xffff ? 3 : 4, width: cp > 0xffff ? 2 : 1 };
}

export function liveLogUTF8Bytes(text: string): number {
  let bytes = 0;
  for (let index = 0; index < text.length;) {
    const next = utf8SizeAt(text, index);
    bytes += next.size;
    index += next.width;
  }
  return bytes;
}

function prefixEnd(text: string, start: number, maxBytes: number): number {
  let bytes = 0;
  let end = start;
  while (end < text.length) {
    const next = utf8SizeAt(text, end);
    if (bytes + next.size > maxBytes) break;
    bytes += next.size;
    end += next.width;
  }
  return end;
}

function codePointStart(text: string, index: number): number {
  if (index === 0) return 0;
  const current = text.charCodeAt(index);
  const previous = text.charCodeAt(index - 1);
  return current >= 0xdc00 && current <= 0xdfff && previous >= 0xd800 && previous <= 0xdbff ? index - 1 : index;
}

export interface LiveLogExcerpt {
  text: string;
  start: number;
  end: number;
  prefixOmitted: boolean;
  suffixOmitted: boolean;
}

export function liveLogExcerpt(text: string, query = "", selected = false): LiveLogExcerpt {
  let start = 0;
  if (selected && query) {
    const match = text.indexOf(query);
    if (match >= 0) start = codePointStart(text, match);
  }
  const end = prefixEnd(text, start, MAX_LIVE_LOG_DISPLAY_BYTES);
  return { text: text.slice(start, end), start, end, prefixOmitted: start > 0, suffixOmitted: end < text.length };
}

export function sliceLiveLogRuns(runs: LiveLogTextRun[], start: number, end: number): LiveLogTextRun[] {
  const out: LiveLogTextRun[] = [];
  let offset = 0;
  for (const run of runs) {
    const runEnd = offset + run.text.length;
    if (runEnd > start && offset < end) {
      out.push({ ...run, text: run.text.slice(Math.max(0, start - offset), Math.min(run.text.length, end - offset)) });
    }
    offset = runEnd;
    if (offset >= end) break;
  }
  return out;
}
